/**
 * Fixture mode — the guarantee behind the preview gallery (#25 / R5.3).
 *
 * `redesign/proposals/astra/tokens.json` states the contract in one line:
 *   "fixture": "Persistent preview notice; zero network mutations; resets on navigation"
 *
 * All three halves are enforced here rather than trusted:
 *
 * 1. **Persistent notice** — `FixtureBanner` renders on every fixture route and cannot be
 *    dismissed. A preview that looks like the real application is how a screenshot of fake data
 *    ends up in a status report.
 * 2. **Zero network mutations** — `assertNoMutation` throws before a request leaves the browser.
 *    The API client calls it, so a fixture surface physically cannot POST.
 * 3. **Resets on navigation** — the gallery links carry `replace` semantics and every surface is
 *    lazily mounted fresh, so leaving a surface drops its local state.
 *
 * Why a module-level flag rather than React context: the API client is not a component and cannot
 * read context. This is deliberately the smallest thing that can enforce rule 2.
 */

let active = false;

/** True while the user is inside the preview gallery. */
export function isFixtureMode() {
  return active;
}

export function enterFixtureMode() {
  active = true;
}

export function exitFixtureMode() {
  active = false;
}

export class FixtureMutationError extends Error {
  constructor(path, method) {
    super(
      `fixture preview refused ${method} ${path}: the gallery makes zero network mutations, so this ` +
        'surface is showing preview data and must not write anything',
    );
    this.name = 'FixtureMutationError';
    this.path = path;
    this.method = method;
  }
}

/** Called by the API client before every mutating request. */
export function assertNoMutation(path, method) {
  if (active) throw new FixtureMutationError(path, method);
}
